import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { useErrorHandler, APIError, ClientError, useQueryClient, useLoaderToast, useJobStatus } from 'shared-ui';

import { addGroup } from 'api/groups/groups';
import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { useMutation } from 'hooks/useMutation/useMutation';
import { GroupTypeEnum } from 'api/groups/groups.enum';
import { DEFAULT_PERMISSIONS } from 'core/global.variables';
import { useResetPermissionsFormValues } from 'hooks/useResetPermissionsFormValues/useResetPermissionsFormValues';
import { useModalState } from 'hooks/useModalState/useModalState';
import { isCreateGroupJobResponse } from 'app/groups/utils/groupUpdateHelpers';

import { useAddGroupValidation } from './AddGroup.validation';
import { AddGroupFormBody } from './AddGroup.types';
import { GlcValueEnum } from './AddGroup.enum';
import { AddGroup } from './AddGroup';

export const AddGroupContainer = () => {
  const { formatMessage } = useLocale();
  const { schema } = useAddGroupValidation();
  const queryClient = useQueryClient();
  const { isModalOpen, onModalOpen, onModalClose } = useModalState();
  const { handleError } = useErrorHandler();

  const methods = useForm<AddGroupFormBody>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      ...DEFAULT_PERMISSIONS,
      tkds: [],
      idGlcs: [],
      typ: GroupTypeEnum.STRUKTURALNA,
      glc: GlcValueEnum.NO,
    },
  });

  const { load: loadAddGroup, update: updateLoadAddGroup, close: closeLoadAddGroup } = useLoaderToast('addGroup');

  const handleJobComplete = () => {
    updateLoadAddGroup(
      formatMessage({
        id: AppMessages[
          methods.watch('typ') === GroupTypeEnum.STRUKTURALNA
            ? 'groups.addGroup.structural.successMessage'
            : 'groups.addGroup.personal.successMessage'
        ],
      }),
    );

    queryClient.invalidateQueries([QueryKeyEnum.GROUPS]);
  };

  const handleJobError = () => {
    toast.error(formatMessage({ id: AppMessages['groups.addGroup.async.errorMessage'] }));
  };

  const jobStatus = useJobStatus({
    useGlobalModal: true,
    autoClose: false,
    onComplete: handleJobComplete,
    onError: handleJobError,
    showItemCount: false,
    customCompleteMessage: formatMessage({ id: AppMessages['groups.result.success'] }),
    customFailedMessage: formatMessage({ id: AppMessages['groups.result.failed'] }),
    customActiveMessage: formatMessage({ id: AppMessages['groups.result.waiting'] }),
    title: formatMessage({ id: AppMessages['groups.result.addTitle'] }),
  });

  const { mutate: onAddGroup, isLoading: isLoadingAddGroup } = useMutation(addGroup, {
    onMutate: () => loadAddGroup(formatMessage({ id: AppMessages['groups.addGroup.loader'] })),
    onSuccess: (response, { body }) => {
      if (isCreateGroupJobResponse(response)) {
        jobStatus.startJob(response.jobId);

        onModalClose();
      } else {
        queryClient.invalidateQueries([QueryKeyEnum.GROUPS]);
        onModalClose();

        updateLoadAddGroup(
          formatMessage({
            id: AppMessages[
              body.typ === GroupTypeEnum.STRUKTURALNA
                ? 'groups.addGroup.structural.successMessage'
                : 'groups.addGroup.personal.successMessage'
            ],
          }),
        );
      }
    },
    onError: (error: ClientError<APIError>) => {
      const errorFields = error.response?.data.fields;

      const isGroupIdExistsError = errorFields?.find(({ type, field }) => type === 'exists' && field === 'idGrupy');

      if (isGroupIdExistsError) {
        closeLoadAddGroup();
        return methods.setError('idGrupy', {
          message: formatMessage({ id: AppMessages['groups.addGroup.groupDefinition.field.validation.id'] }),
        });
      }

      handleError({ error, omitAPIErrorMessage: true, updateToastCallback: updateLoadAddGroup });
    },
  });

  const onSubmit = ({ pkds, ...body }: AddGroupFormBody) => {
    onAddGroup({
      body: {
        ...body,
        // The API field is `pkd` (CreateGrupaRequest); `pkds` was silently dropped by the backend.
        pkd: pkds?.map(({ value }) => ({ id: `${value}` })),
        tkds: body.tkds?.map(({ value }) => ({ id: `${value}` })),
        idGlcs: body.idGlcs?.map(({ value }) => ({ id: `${value}` })),
        glc: body.glc === GlcValueEnum.YES ? true : false,
      },
    });
  };

  useEffect(() => {
    methods.reset();
  }, [isModalOpen]);

  useEffect(() => {
    methods.setValue('pkds', undefined);
    methods.setValue('komorki', undefined);
  }, [methods.watch('typ')]);

  useResetPermissionsFormValues({
    selectedValues: methods.watch('tkds') || methods.watch('idGlcs'),
    setFormValue: methods.setValue,
  });

  return (
    <FormProvider {...methods}>
      <AddGroup
        isModalOpen={isModalOpen}
        onModalClose={onModalClose}
        onModalOpen={onModalOpen}
        onSubmit={onSubmit}
        isLoadingAddGroup={isLoadingAddGroup}
      />
    </FormProvider>
  );
};
